import { NgTemplateOutlet } from '@angular/common'
import { Component, input, OnChanges, OnInit, SimpleChanges, TemplateRef } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { FieldNameComponent } from '@app/shared/field-name/field-name.component'
import { TbIconComponent } from '@components-library/tb-icon/tb-icon.component'
import { getBoolean } from '@core/global-util'
import { AppRecord, Field, FieldType, isWorkspaceGlobal, Workspace } from '@core/models'
import { InputContainerAppearance } from '@models/input'
import { Dictionary } from '@ngrx/entity'
import { isEqual } from 'lodash-es'
import { RecordFieldsInputComponent } from './record-fields-input/record-fields-input.component'

@Component({
    selector: 'app-record-fields',
    standalone: true,
    templateUrl: './record-fields.component.html',
    styleUrls: ['record-fields.component.sass'],
    imports: [
        ReactiveFormsModule,
        RecordFieldsInputComponent,
        FieldNameComponent,
        TbIconComponent,
        NgTemplateOutlet,
    ],
})
export class RecordFieldsComponent implements OnInit, OnChanges {
    readonly workspacesDictionary = input.required<{ [workspaceGuid: string]: Workspace }>()
    readonly fields = input.required<Field[]>()
    readonly fieldTypes = input.required<Dictionary<FieldType>>()
    readonly values = input.required<Record<string, string | null | undefined>>()
    readonly record = input<AppRecord>()
    readonly formDirectiveGroup = input<FormGroup>()
    readonly inputAppearance = input(InputContainerAppearance.create_record)
    readonly groupName = input.required<string>()
    readonly hoverIcons = input<TemplateRef<any>>()
    readonly showSystemFields = input(false)

    formGroup!: FormGroup<{ [fieldGuid: string]: FormControl<string | null> }>
    globalWorkspace?: Workspace

    ngOnInit() {
        this.setFormGroup()
        this.globalWorkspace = this.getGlobalWorkspace()
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.fields) {
            if (!isEqual(changes.fields.currentValue, changes.fields.previousValue)) {
                this.setFormGroup()
                return
            }
        }

        if (changes.values && !changes.values.firstChange) {
            this.updateValues()
        }
    }

    showSystemField(field: Field) {
        return (
            field.is_readonly &&
            this.showSystemFields() &&
            getBoolean(field.workspace_name?.is_global) &&
            this.globalWorkspace
        )
    }

    getFieldFormControl(guid: string) {
        return this.formGroup.controls[guid]
    }

    private updateValues() {
        this.fields().forEach((field) => {
            const newValue = this.values()[field.guid] ?? null
            const oldValue = this.formGroup.controls[field.guid].value
            if (oldValue === newValue) return

            this.formGroup.controls[field.guid].setValue(newValue, {
                emitEvent: false,
            })
        })
    }

    private setFormGroup() {
        this.formGroup = new FormGroup(this.setFormControls())

        this.formDirectiveGroup()?.setControl(this.groupName(), this.formGroup)
    }

    private getGlobalWorkspace() {
        const globalWorkspaceGuid = Object.keys(this.workspacesDictionary()).find((workspaceGuid) =>
            isWorkspaceGlobal(this.workspacesDictionary()[workspaceGuid]),
        )
        if (!globalWorkspaceGuid) return
        return this.workspacesDictionary()[globalWorkspaceGuid]
    }

    private setFormControls() {
        return this.fields().reduce(
            (controls: { [fieldGuid: string]: FormControl<string | null> }, field: Field) => {
                const validators = getBoolean(field.is_required) ? [Validators.required] : []
                controls[field.guid] = new FormControl(
                    this.values()[field.guid] ?? null,
                    validators,
                )
                return controls
            },
            {} as { [fieldGuid: string]: FormControl<string | null> },
        )
    }
}
